import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Trophy, X, Share2, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * /wildcard-story — Instagram-Stories style swipeable card deck.
 * One candidate per screen, 9:16 portrait, tap-right = next, tap-left = prev.
 * Auto-advance every 6s with a progress bar per candidate.
 * Public — no login required.
 */
const AUTO_MS = 6000;

export default function WildCardStory() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isAdmin = user && ["super_admin", "league_admin"].includes(user.role);

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/exports/wildcard-stories.zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "mara-wildcard-stories.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Wildcard stories ZIP downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download ZIP");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/qf/wildcard-report");
        if (alive) setRows(data.candidates || []);
      } catch { /* hidden */ }
    })();
    return () => { alive = false; };
  }, []);

  const next = useCallback(() => {
    setProgress(0);
    setIdx((i) => (rows.length ? (i + 1) % rows.length : 0));
  }, [rows.length]);

  const prev = useCallback(() => {
    setProgress(0);
    setIdx((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
  }, [rows.length]);

  useEffect(() => {
    if (paused || !rows.length) return;
    const step = 100 / (AUTO_MS / 50);
    const t = setInterval(() => {
      setProgress((p) => {
        if (p + step >= 100) { next(); return 0; }
        return p + step;
      });
    }, 50);
    return () => clearInterval(t);
  }, [paused, rows.length, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === " ") setPaused((p) => !p);
      else if (e.key === "Escape") window.location.assign("/standings");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const share = async () => {
    try {
      const c = rows[idx];
      const text = `${c.team_name} · Wildcard Race Rank #${c.rank} · AGD ${c.avg_game_diff.toFixed(2)} · Mara Padel League S3`;
      if (navigator.share) await navigator.share({ title: "Mara Wildcard", text, url: window.location.href });
      else { await navigator.clipboard.writeText(`${text}\n${window.location.href}`); toast.success("Copied to clipboard"); }
    } catch { /* user cancelled */ }
  };

  if (!rows.length) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500" data-testid="wildcard-story-empty">
        <div className="text-center">
          <div className="text-2xl uppercase tracking-widest mb-2">Wildcard story not available</div>
          <Link to="/standings" className="text-[#F4B400] underline">Back to standings</Link>
        </div>
      </div>
    );
  }

  const c = rows[idx];
  const isPicked = !!c.picked;
  const tone = isPicked
    ? "from-[#F4B400] via-[#e5aa00] to-[#c68f00]"
    : c.rank <= 2 ? "from-[#00A86B] via-[#008d5c] to-[#00734b]"
    : "from-[#2a3a34] via-[#1a2822] to-[#0f1a15]";

  return (
    <div className="min-h-screen bg-black flex items-center justify-center overflow-hidden" data-testid="wildcard-story-page">
      {/* Portrait card */}
      <div className="relative w-full max-w-md h-screen md:h-[90vh] md:rounded-3xl overflow-hidden bg-black shadow-[0_0_60px_-10px_rgba(244,180,0,0.4)]">
        {/* Background gradient tied to card status */}
        <div className={`absolute inset-0 bg-gradient-to-br ${tone} opacity-90`} />
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0.5px, transparent 0.5px), radial-gradient(circle at 80% 60%, #fff 0.5px, transparent 0.5px)", backgroundSize: "24px 24px" }} />

        {/* Progress bars — one per candidate */}
        <div className="absolute top-3 left-3 right-3 flex gap-1 z-20" data-testid="wildcard-story-progress">
          {rows.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/20 rounded overflow-hidden">
              <div className="h-full bg-white transition-all"
                style={{ width: i < idx ? "100%" : i === idx ? `${progress}%` : "0%" }} />
            </div>
          ))}
        </div>

        {/* Top bar */}
        <div className="absolute top-6 left-4 right-4 flex items-center justify-between z-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 font-bold">Wildcard Race · S3</div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button
                onClick={downloadZip}
                disabled={downloading}
                className="text-white/80 hover:text-white disabled:opacity-40"
                aria-label="Download all stories as ZIP"
                data-testid="wildcard-story-download-zip-btn"
                title="Download all stories as ZIP"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button onClick={share} className="text-white/80 hover:text-white" aria-label="Share" data-testid="wildcard-story-share-btn">
              <Share2 className="w-4 h-4" />
            </button>
            <Link to="/standings" className="text-white/80 hover:text-white" aria-label="Close">
              <X className="w-5 h-5" />
            </Link>
          </div>
        </div>

        {/* Tap zones */}
        <button onClick={prev}
          onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}
          className="absolute left-0 top-0 w-1/3 h-full z-10 focus:outline-none"
          data-testid="wildcard-story-prev-zone"
          aria-label="Previous" />
        <button onClick={next}
          onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}
          className="absolute right-0 top-0 w-1/3 h-full z-10 focus:outline-none"
          data-testid="wildcard-story-next-zone"
          aria-label="Next" />

        {/* Content */}
        <div className="relative z-[5] h-full flex flex-col justify-center items-center p-8 text-white text-center" data-testid={`wildcard-story-card-${c.rank}`}>
          {/* Rank ring */}
          <div className={`w-40 h-40 rounded-full flex items-center justify-center border-4 ${isPicked ? "border-white/60" : "border-white/20"} bg-black/40 mb-6`}>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Rank</div>
              <div className="font-anton text-7xl leading-none">#{c.rank}</div>
            </div>
          </div>

          <div className="font-anton text-5xl uppercase tracking-tight mb-2 max-w-xs truncate">
            {c.team_name}
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-6">
            Group {c.group} · Position {c.position_in_group}
          </div>

          {/* AGD hero */}
          <div className="my-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">Average Game Diff</div>
            <div className="font-anton text-6xl tabular-nums leading-none">
              {c.avg_game_diff > 0 ? "+" : ""}{c.avg_game_diff.toFixed(2)}
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-3 w-full max-w-xs mt-4">
            <Stat label="Games W-L" value={`${c.games_won}-${c.games_lost}`} />
            <Stat label="GD" value={`${c.game_diff > 0 ? "+" : ""}${c.game_diff}`} />
            <Stat label="Ties" value={c.ties_played} />
          </div>

          {/* Verdict */}
          <div className="mt-8">
            {isPicked ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-bold uppercase tracking-widest text-xs">
                <Trophy className="w-4 h-4" /> {c.picked}
              </div>
            ) : (
              <div className="text-xs uppercase tracking-widest text-white/60">Did not make the cut</div>
            )}
          </div>

          {/* Nav hint */}
          <div className="absolute bottom-6 left-0 right-0 flex items-center justify-between px-8 text-white/50 text-[10px] uppercase tracking-widest">
            <ChevronLeft className="w-3 h-3" /> Tap or ← →
            <ChevronRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-black/30 rounded-lg py-2 border border-white/10">
      <div className="text-[8px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="font-anton text-2xl tabular-nums">{value}</div>
    </div>
  );
}
